import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  MetricCard,
  Spinner,
} from '@pnl/ui';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtUsd(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
}

type PayoutPeriod = 'today' | 'week' | 'month' | 'year' | 'custom';

/** Início do dia BRT (00:00 America/Sao_Paulo = 03:00 UTC) como instante UTC. */
function brtDayStart(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m, d, 3, 0, 0));
}

/** Intervalo [from,to] em ISO pro período escolhido (boundaries em BRT). */
function payoutRange(
  period: PayoutPeriod,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  const now = new Date();
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  const y = brt.getUTCFullYear();
  const m = brt.getUTCMonth();
  const d = brt.getUTCDate();
  const dow = brt.getUTCDay(); // 0=domingo
  if (period === 'custom') {
    const range: { from?: string; to?: string } = {};
    if (customFrom) {
      const [yy, mm, dd] = customFrom.split('-').map(Number);
      range.from = brtDayStart(yy!, mm! - 1, dd!).toISOString();
    }
    if (customTo) {
      const [yy, mm, dd] = customTo.split('-').map(Number);
      // fim do dia = início do dia seguinte (BRT)
      range.to = brtDayStart(yy!, mm! - 1, dd! + 1).toISOString();
    }
    return range;
  }
  let from: Date;
  if (period === 'today') from = brtDayStart(y, m, d);
  else if (period === 'week')
    from = brtDayStart(y, m, d - ((dow + 6) % 7)); // segunda
  else if (period === 'year') from = brtDayStart(y, 0, 1);
  else from = brtDayStart(y, m, 1); // month
  // Sem `to` (saque PAID nunca é no futuro = "de `from` até agora"). Manter o
  // intervalo estável entre renders evita refetch em loop (queryKey não muda).
  return { from: from.toISOString() };
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const summary = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.getAdminDashboard(),
    refetchInterval: 60_000,
  });
  const s = summary.data;
  const n = (v: number | undefined): string => (summary.isLoading ? '…' : String(v ?? 0));

  return (
    <div className="space-y-8">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
          Visão geral
        </p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Olá, {user?.fullName || user?.email?.split('@')[0] || 'Admin'} 👋
        </h1>
        <p className="text-ink-500 mt-1 text-sm">Snapshot da operação em tempo real.</p>
      </div>

      {summary.isError && (
        <Alert tone="danger">
          Não foi possível carregar os indicadores. Os números abaixo podem estar zerados — tente
          recarregar a página.
        </Alert>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Traders ativos" value={n(s?.tradersActive)} hint="contas no sistema" />
        <MetricCard
          label="Saques pendentes"
          value={n(s?.withdrawalsPending)}
          hint="aprovar até a janela"
        />
        <MetricCard
          label="Receita do mês (BRL)"
          value={summary.isLoading ? '…' : fmtBrl(s?.monthRevenueBrlCents ?? 0)}
          hint="faturas pagas no mês"
        />
        <MetricCard
          label="Inadimplências"
          value={n(s?.delinquencies)}
          hint="assinatura + plataforma"
        />
        <MetricCard label="Contas funded" value={n(s?.fundedAccounts)} hint="funded + live" />
        <MetricCard
          label="Assinaturas ativas"
          value={n(s?.activeSubscriptions)}
          hint="planos recorrentes"
        />
        <MetricCard
          label="Replicadores ativos"
          value={n(s?.activeReplicators)}
          hint="mensalidade paga"
        />
      </section>

      <RevenueCard />

      <PayoutsCard />

      <section>
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Suas permissões nesta sessão</CardTitle>
              <CardDescription>
                Total:{' '}
                <span className="font-mono font-semibold">{user?.permissions.length ?? 0}</span> ·
                Roles: {user?.roles.join(', ') || '—'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardBody>
            {user?.permissions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {user.permissions.map((p) => (
                  <Badge key={p} tone="brand">
                    {p}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-ink-500 text-sm">Nenhuma permissão atribuída.</p>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

const PERIODS: { key: PayoutPeriod; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Esta semana' },
  { key: 'month', label: 'Este mês' },
  { key: 'year', label: 'Este ano' },
  { key: 'custom', label: 'Personalizado' },
];

/** Seletor de período compartilhado pelos cards Receita e Repasses. */
function PeriodPicker({
  period,
  onPeriodChange,
  from,
  onFromChange,
  to,
  onToChange,
}: {
  period: PayoutPeriod;
  onPeriodChange: (p: PayoutPeriod) => void;
  from: string;
  onFromChange: (v: string) => void;
  to: string;
  onToChange: (v: string) => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {PERIODS.map((p) => (
        <Button
          key={p.key}
          size="sm"
          variant={period === p.key ? 'primary' : 'secondary'}
          onClick={() => onPeriodChange(p.key)}
        >
          {p.label}
        </Button>
      ))}
      {period === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => onFromChange(e.target.value)}
            className="border-ink-200 text-ink-900 focus:border-brand-500 rounded-md border bg-white px-2 py-1 text-sm focus:outline-none"
          />
          <span className="text-ink-400 text-xs">até</span>
          <input
            type="date"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            className="border-ink-200 text-ink-900 focus:border-brand-500 rounded-md border bg-white px-2 py-1 text-sm focus:outline-none"
          />
        </div>
      )}
    </div>
  );
}

function RevenueCard() {
  const [period, setPeriod] = useState<PayoutPeriod>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = payoutRange(period, from, to);
  const ready = period !== 'custom' || Boolean(range.from || range.to);

  const q = useQuery({
    queryKey: ['admin', 'revenue', period, range.from ?? '', range.to ?? ''],
    queryFn: () => api.getRevenueTotal(range),
    enabled: ready,
  });

  return (
    <section>
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Receita</CardTitle>
            <CardDescription>Faturas pagas no período — Stripe + Mercado Pago.</CardDescription>
          </div>
        </CardHeader>
        <CardBody>
          <PeriodPicker
            period={period}
            onPeriodChange={setPeriod}
            from={from}
            onFromChange={setFrom}
            to={to}
            onToChange={setTo}
          />

          {!ready ? (
            <p className="text-ink-500 text-sm">Escolha as datas do período personalizado.</p>
          ) : q.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : q.isError ? (
            <Alert tone="danger">Não foi possível carregar a receita.</Alert>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="border-ink-100 rounded-lg border p-4">
                <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">
                  B3 (BRL)
                </p>
                <p className="text-ink-900 mt-1 text-2xl font-semibold tabular-nums">
                  {fmtBrl(q.data?.b3BrlCents ?? 0)}
                </p>
              </div>
              <div className="border-ink-100 rounded-lg border p-4">
                <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">
                  Forex (USD)
                </p>
                <p className="text-ink-900 mt-1 text-2xl font-semibold tabular-nums">
                  {fmtUsd(q.data?.forexUsdCents ?? 0)}
                </p>
                {(q.data?.forexBrlCents ?? 0) > 0 && (
                  <p className="text-ink-500 mt-1 text-xs">
                    + {fmtBrl(q.data?.forexBrlCents ?? 0)} em parcelas BRL
                  </p>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </section>
  );
}

function PayoutsCard() {
  const [period, setPeriod] = useState<PayoutPeriod>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const range = payoutRange(period, from, to);
  const ready = period !== 'custom' || Boolean(range.from || range.to);

  const q = useQuery({
    queryKey: ['admin', 'payouts', period, range.from ?? '', range.to ?? ''],
    queryFn: () => api.getPayoutsTotal(range),
    enabled: ready,
  });

  return (
    <section>
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Total de repasses aos traders</CardTitle>
            <CardDescription>
              Líquido dos saques confirmados (após 20% mesa e 20% IR) no período.
            </CardDescription>
          </div>
        </CardHeader>
        <CardBody>
          <PeriodPicker
            period={period}
            onPeriodChange={setPeriod}
            from={from}
            onFromChange={setFrom}
            to={to}
            onToChange={setTo}
          />

          {!ready ? (
            <p className="text-ink-500 text-sm">Escolha as datas do período personalizado.</p>
          ) : q.isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : q.isError ? (
            <Alert tone="danger">Não foi possível carregar os repasses.</Alert>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="border-ink-100 rounded-lg border p-4">
                <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">
                  B3 (BRL)
                </p>
                <p className="text-ink-900 mt-1 text-2xl font-semibold tabular-nums">
                  {fmtBrl(q.data?.brlNetCents ?? 0)}
                </p>
              </div>
              <div className="border-ink-100 rounded-lg border p-4">
                <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">
                  Forex (USD)
                </p>
                <p className="text-ink-900 mt-1 text-2xl font-semibold tabular-nums">
                  {fmtUsd(q.data?.usdNetCents ?? 0)}
                </p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </section>
  );
}
